const DEFAULT_PIXELS = '1186542702419638,2173133966515009'
const DEFAULT_GA = 'G-WFK99231Q3'

const PIXEL_IDS = (
  import.meta.env.VITE_META_PIXEL_IDS || DEFAULT_PIXELS
)
  .split(',')
  .map((id) => id.trim())
  .filter(Boolean)

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || DEFAULT_GA

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[]
  loaded: boolean
  version: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

function loadScript(src: string, id: string) {
  if (document.getElementById(id)) return
  const script = document.createElement('script')
  script.id = id
  script.async = true
  script.src = src
  document.head.appendChild(script)
}

function ensureMeta() {
  if (typeof window === 'undefined' || PIXEL_IDS.length === 0) return
  if (window.fbq) return
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue.push(args)
  } as Fbq
  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  window.fbq = fbq
  window._fbq = fbq
  loadScript('https://connect.facebook.net/en_US/fbevents.js', 'meta-fbevents')
  for (const id of PIXEL_IDS) fbq('init', id)
}

function ensureGa() {
  if (typeof window === 'undefined' || !GA_ID) return
  if (window.gtag) return
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args)
  }
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, 'ga4')
  window.gtag('js', new Date())
  window.gtag('config', GA_ID)
}

type AnalyticsCapture = (
  event: string,
  properties?: Record<string, unknown>,
) => void

type LandingName = 'grocery' | 'join' | 'wtf'

let analyticsCapture: AnalyticsCapture | null = null
let landingName: LandingName | null = null

export function setAnalyticsCapture(
  capture: AnalyticsCapture,
  landing?: LandingName,
) {
  analyticsCapture = capture
  if (landing) landingName = landing
}

function landingProps() {
  const path = window.location.pathname.replace(/\/$/, '') || '/'
  const host = window.location.hostname
  const landing =
    landingName ??
    (host.startsWith('wtf.') || path.startsWith('/wtf')
      ? 'wtf'
      : path.startsWith('/grocery')
        ? 'grocery'
        : path.startsWith('/join')
          ? 'join'
          : null)
  const isFgc = landing === 'grocery' || landing === 'join'
  return {
    landing: host.startsWith('wtf.') ? 'https://wtf.bloomagrows.com' : path,
    landing_page: landing,
    ...(isFgc
      ? { value: 29, currency: 'USD', content_name: 'Fall Garden Club' }
      : landing === 'wtf'
        ? { content_name: 'Plant Troubleshooter' }
        : {}),
  }
}

function capture(event: string, properties?: Record<string, unknown>) {
  const props = { ...landingProps(), ...properties }
  const name = props.landing_page ? `${props.landing_page}_${event}` : event
  analyticsCapture?.(name, props)
}

export function trackEvent(event: string, properties?: Record<string, unknown>) {
  capture(event, properties)
}

export function trackLandingPage() {
  ensureMeta()
  ensureGa()
  window.fbq?.('track', 'PageView')
  capture('landing_view')
}

export function trackSuccessPage() {
  ensureMeta()
  window.fbq?.('track', 'PageView')
  capture('success_view')
}

export function trackInitiateCheckout() {
  ensureMeta()
  window.fbq?.('track', 'InitiateCheckout', {
    value: 29,
    currency: 'USD',
    content_name: 'Fall Garden Club',
  })
  capture('initiate_checkout')
}

const firedPurchases = new Set<string>()

export function trackPurchase(sessionId: string) {
  const key = `fgc_purchase_${sessionId}`
  if (!sessionId || firedPurchases.has(sessionId) || sessionStorage.getItem(key)) {
    return
  }
  firedPurchases.add(sessionId)
  sessionStorage.setItem(key, '1')
  ensureMeta()
  ensureGa()
  window.fbq?.('track', 'Purchase', {
    value: 29,
    currency: 'USD',
    content_name: 'Fall Garden Club',
    content_type: 'product',
  })
  window.gtag?.('event', 'purchase', {
    transaction_id: sessionId,
    value: 29,
    currency: 'USD',
    items: [{ item_name: 'Fall Garden Club', price: 29, quantity: 1 }],
  })
  capture('purchase', {
    transaction_id: sessionId,
    content_type: 'product',
  })
}

export function successPath() {
  const path = window.location.pathname.replace(/\/$/, '')
  if (!path || path === '/') return '/success'
  if (path.endsWith('/success')) return path
  // Purchase LPs share the same thanks / Purchase pixel URL.
  if (path === '/join' || path === '/join-now') return '/join/success'
  if (path === '/grocery') return '/grocery'
  return `${path}/success`
}
